import { Link } from '@inertiajs/react';
import clsx from 'clsx';
import React from 'react';

type ButtonSize = 'sm' | 'md' | 'full-width';

interface Props {
    type?: 'submit' | 'button' | 'reset' | undefined;
    className?: string;
    onClick?: () => void;
    children: React.ReactNode;
    disabled?: boolean;
    loading?: boolean;
    size?: ButtonSize;
    as?: 'button' | 'link';
    href?: string;
    target?: string;
}

const SecondaryButton: React.FC<Props> = ({
    type = 'button',
    className = '',
    onClick,
    children,
    disabled = false,
    loading = false,
    size = 'md',
    as = 'button',
    href,
    target,
}) => {
    // Neutral styling that works across themes
    const baseClasses = 'inline-flex items-center justify-center gap-x-2 rounded-lg border border-gray-300 bg-white font-medium text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50 hover:border-gray-400 hover:shadow focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    // Size classes mapping
    const sizeClasses: Record<ButtonSize, string> = {
        'sm': 'px-3 py-1 text-xs',
        'md': 'px-4 py-2 text-sm',
        'full-width': 'w-full px-4 py-2 text-sm',
    };

    const combinedClasses = clsx(
        baseClasses,
        sizeClasses[size],
        className
    );

    if (as === 'link' && target === '_blank' && href) {
        return (
            <a href={href || ''} className={combinedClasses} target={target}>
                {children}
            </a>
        );
    }

    if (as === 'link') {
        return (
            <Link href={href || ''} className={combinedClasses} target={target}>
                {children}
            </Link>
        );
    }

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled || loading}
            className={combinedClasses}
        >
            {loading && (
                <svg
                    aria-hidden="true"
                    role="status"
                    className="mr-2 inline h-4 w-4 animate-spin text-gray-400"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                    ></path>
                    <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentColor"
                    ></path>
                </svg>
            )}
            {children}
        </button>
    );
};

export default SecondaryButton;
